import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  Typography,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Tooltip,
  Slider,
  useMediaQuery,
  useTheme,
  Slide,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { forwardRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useNotifications, Notification } from '../hooks/useNotifications';
import { formatRelativeTimeInNY } from '../utils/dateUtils';
import { apiRequest } from '../utils/api';
import type { User } from '../types';

const SlideUp = forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface NotificationListProps {
  open: boolean;
  onClose: () => void;
}

export default function NotificationList({ open, onClose }: NotificationListProps) {
  const { notifications, loading, markAsRead, markAllAsRead, deleteNotification, deleteAllNotifications, loadNotifications, notificationVolume, setNotificationVolume } = useNotifications();
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiRequest('/users/profile/me', { method: 'GET' });
        setCurrentUser(response.data as User);
      } catch (error) {
        console.error('Failed to load current user:', error);
      }
    };
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open, loadNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    const isTaskNotification = notification.notification_type === 'task';
    const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';

    if (isTaskNotification && !isFieldWorker) {
      onClose();
      navigate('/schedule');
      return;
    }

    if (notification.job_id) {
      onClose();
      navigate(`/jobs/${notification.job_id}#chat`);
    } else {
      setSelectedNotification(notification);
    }
  };

  const handleDelete = async (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    await deleteNotification(notificationId);
    if (selectedNotification?.id === notificationId) {
      setSelectedNotification(null);
    }
  };

  const unreadNotifications = notifications.filter(n => !n.is_read);
  const readNotifications = notifications.filter(n => n.is_read);

  const NotificationItem = ({ notification, isUnread }: { notification: Notification; isUnread: boolean }) => (
    <ListItem
      onClick={() => handleNotificationClick(notification)}
      disablePadding
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        bgcolor: isUnread ? 'action.hover' : 'transparent',
        borderLeft: isUnread ? '3px solid' : '3px solid transparent',
        borderColor: isUnread ? 'primary.main' : 'transparent',
        opacity: isUnread ? 1 : 0.72,
        '&:hover': { bgcolor: 'action.selected' },
        // Ensure good tap target height on mobile
        minHeight: 56,
      }}
    >
      {/* Top row: title + chip + delete */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            component="span"
            fontWeight={isUnread ? 700 : 500}
            noWrap
          >
            {notification.title || 'New notification'}
          </Typography>
          {isUnread && (
            <Chip
              label="New"
              size="small"
              color="primary"
              sx={{ height: 18, fontSize: '0.62rem', flexShrink: 0 }}
            />
          )}
        </Box>
        <Tooltip title="Delete">
          <IconButton
            onClick={(e) => handleDelete(e, notification.id)}
            size={isMobile ? 'medium' : 'small'}
            sx={{ flexShrink: 0, color: 'text.secondary' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Message */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 0.25, lineHeight: 1.4, wordBreak: 'break-word' }}
      >
        {notification.message}
      </Typography>

      {/* Meta row — wraps on small screens */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          mt: 0.5,
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {formatRelativeTimeInNY(notification.created_at)}
        </Typography>
        {notification.sender_name && (
          <>
            <Typography variant="caption" color="text.secondary">·</Typography>
            <Typography variant="caption" color="text.secondary">
              From: {notification.sender_name}
            </Typography>
          </>
        )}
        {notification.job_number && (
          <>
            <Typography variant="caption" color="text.secondary">·</Typography>
            <Typography variant="caption" color="primary.main" fontWeight={600}>
              {notification.job_number}
            </Typography>
          </>
        )}
      </Box>
    </ListItem>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      TransitionComponent={isMobile ? SlideUp : undefined}
      slotProps={{
        paper: {
          sx: {
            ...(!isMobile && {
              height: '80vh',
              maxHeight: 600,
              borderRadius: 2,
            }),
            ...(isMobile && {
              m: 0,
              mt: 'auto',
              height: '90dvh',
              borderRadius: '16px 16px 0 0',
            }),
          },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        component="div"
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          {/* Pull handle indicator on mobile */}
          {isMobile && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 36,
                height: 4,
                borderRadius: 2,
                bgcolor: 'divider',
              }}
            />
          )}

          <Typography variant="h6" fontWeight={600}>
            Notifications
            {unreadNotifications.length > 0 && (
              <Chip
                label={unreadNotifications.length}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {unreadNotifications.length > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton onClick={markAllAsRead} size="small" color="primary">
                  <CheckCircleIcon fontSize={isMobile ? 'medium' : 'small'} />
                </IconButton>
              </Tooltip>
            )}
            {notifications.length > 0 && (
              <Tooltip title="Delete all">
                <IconButton onClick={deleteAllNotifications} size="small" color="error">
                  <DeleteSweepIcon fontSize={isMobile ? 'medium' : 'small'} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={notificationVolume === 0 ? 'Sound off' : `Volume: ${Math.round(notificationVolume * 100)}%`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setNotificationVolume(notificationVolume === 0 ? 0.5 : 0)}
                  color={notificationVolume === 0 ? 'default' : 'primary'}
                >
                  {notificationVolume === 0
                    ? <VolumeOffIcon fontSize={isMobile ? 'medium' : 'small'} />
                    : <VolumeUpIcon fontSize={isMobile ? 'medium' : 'small'} />}
                </IconButton>
                <Slider
                  size="small"
                  value={notificationVolume}
                  min={0}
                  max={1}
                  step={0.05}
                  onChange={(_, value) => setNotificationVolume(value as number)}
                  sx={{ width: 60 }}
                />
              </Box>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize={isMobile ? 'medium' : 'small'} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 0, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 8, px: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {unreadNotifications.map((n) => (
              <NotificationItem key={n.id} notification={n} isUnread={true} />
            ))}
            {unreadNotifications.length > 0 && readNotifications.length > 0 && (
              <Divider sx={{ my: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Earlier
                </Typography>
              </Divider>
            )}
            {readNotifications.map((n) => (
              <NotificationItem key={n.id} notification={n} isUnread={false} />
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
